import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "criminal-defense-lawyer-web-design-colorado",
  metaTitle: "Criminal Defense Lawyer Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom criminal defense lawyer web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Criminal Defense Lawyer Web Design Colorado",
  eyebrow: "Criminal Defense Lawyer Web Design — Colorado",
  h1: (
    <>
      Criminal Defense Lawyer <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded criminal defense lawyer websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Criminal Defense Lawyer websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first criminal defense lawyer sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Criminal Defense Lawyer web design in Colorado",
    body: "Criminal Defense Lawyer web design is the practice of building conversion-focused, mobile-first websites for criminal defense lawyer businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second load — these calls happen at 2 a.m. from a holding cell." },
    { title: "Charge-type service pages", body: "DUI, domestic violence, drug, assault, theft, federal — each ranks independently." },
    { title: "24/7 contact + sticky call", body: "Phone visible on every scroll; emergency form with after-hours routing." },
    { title: "Attorney bio pages", body: "Each attorney with credentials, bar admissions, prior outcomes, and direct call CTA." },
    { title: "Disclaimed case results", body: "Properly disclaimed prior-outcome pages that build trust without violating bar rules." },
    { title: "Schema and SEO baked in", body: "LegalService, Attorney, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Charge-type queries dominate", body: "Searchers type \"DUI lawyer\", \"domestic violence attorney\", not \"criminal defense\". Build for the actual query." },
    { heading: "After-hours capture is the entire game", body: "Most arrest calls come overnight. Sticky click-to-call + 24/7 messaging wins the retainer." },
    { heading: "Trust beats price", body: "Bar credentials, prior outcomes, and reviews above the fold close the consultation." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a criminal defense lawyer website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a criminal defense lawyer website?", a: "Typical websites are built in less than 7 business days. Larger sites, migrations, and custom integrations are scoped before work starts." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs criminal defense lawyer businesses?", a: "No — we build criminal defense lawyer sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Criminal Defense Lawyer Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for criminal defense lawyer businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/criminal-defense-lawyer-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
